import React from 'react';
import { motion } from 'framer-motion';
import { FiMic, FiUsers, FiGlobe, FiCheckCircle } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const PodcastSection = ({ isPageTitle = false }) => {
  // On the podcast page this heading is the page title (H1); elsewhere it stays an H2.
  const Title = isPageTitle ? motion.h1 : motion.h2;

  return (
    <section id="ai-podcast" className="py-24 bg-slate-950 text-white relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <motion.span 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-emerald-400 font-black text-sm uppercase tracking-[0.3em] mb-4 block"
          >
            AI Podcast Creation
          </motion.span>
          <Title 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-extrabold mb-8"
          >
            Launch a <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-400">Global Podcast</span> <br className="hidden md:block" /> Without a Studio
          </Title>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-slate-400 max-w-3xl mx-auto font-medium"
          >
            We use advanced AI to clone your voice and create hyper-realistic avatars. Record once, publish in 40+ languages, and maintain a daily podcast schedule with zero effort.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              icon: FiMic,
              title: "Voice Cloning",
              desc: "Perfectly replicate your voice with all its nuances and emotions. Record in your language, output in any language."
            },
            {
              icon: FiUsers,
              title: "AI Guest Avatars",
              desc: "Host interviews with digital experts or cloned guests without coordinating schedules or time zones."
            },
            {
              icon: FiGlobe,
              title: "Multi-Lingual Reach",
              desc: "Instantly translate your podcast into Spanish, French, Arabic, and more—with perfectly synced lip movements."
            }
          ].map((item, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="p-10 bg-white/5 rounded-[2.5rem] border border-white/10 hover:bg-white/10 transition-all text-center"
            >
              <div className="w-16 h-16 bg-emerald-500 text-slate-950 rounded-2xl flex items-center justify-center mb-8 mx-auto shadow-lg shadow-emerald-500/20">
                <SafeIcon icon={item.icon} className="text-2xl" />
              </div>
              <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
              <p className="text-slate-400 leading-relaxed font-medium">{item.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-20 p-1 bg-gradient-to-r from-emerald-500/20 via-blue-500/20 to-emerald-500/20 rounded-[3rem]">
          <div className="bg-slate-900 rounded-[2.9rem] p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
            <div>
              <p className="text-2xl font-bold mb-2">Ready to start your AI Podcast?</p>
              <p className="text-slate-400">Join 100+ creators dominating the airwaves with AI.</p>
            </div>
            <a 
              href="https://go.digitalgigs.eu/chat" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="px-10 py-5 bg-emerald-500 text-slate-950 font-black rounded-full hover:bg-emerald-400 transition-all shadow-xl shadow-emerald-500/20"
            >
              Get a Free Quote
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PodcastSection;
